import { useState } from 'react';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, Box, Button, CircularProgress, Link, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import authApi from '../../services/authApi';
import FormProvider from '../../components/form/FormProvider';
import TextField from '../../components/form/TextField';
import VerificationCodeField from '../../components/form/VerificationCodeField';
import PasswordField from '../../components/form/PasswordField';
import AuthShell from './components/AuthShell';

const requestCodeSchema = z.object({
  emailAddress: z.email('A valid email address is required'),
});

const verifyAndResetSchema = z.object({
  code: z.string().length(6, 'Enter the 6-digit code'),
  newPassword: z.string().min(8, 'Password must be at least 8 characters'),
  newPasswordConfirmed: z.string().min(1, 'Please confirm your password'),
}).refine(d => d.newPassword === d.newPasswordConfirmed, {
  message: 'Passwords do not match',
  path: ['newPasswordConfirmed'],
});

const CenteredCard = ({ children }) => <AuthShell>{children}</AuthShell>;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialEmail = searchParams.get('email') || '';

  const [forgotPassword] = authApi.endpoints.forgotPassword.useMutation();
  const [verifyForgotPasswordCode] = authApi.endpoints.verifyForgotPasswordCode.useMutation();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [emailAddress, setEmailAddress] = useState(initialEmail);
  const [loading, setLoading] = useState(false);

  const requestMethods = useForm({
    resolver: zodResolver(requestCodeSchema),
    defaultValues: { emailAddress: initialEmail },
  });

  const verifyMethods = useForm({
    resolver: zodResolver(verifyAndResetSchema),
    defaultValues: { code: '', newPassword: '', newPasswordConfirmed: '' },
  });

  const handleRequestCode = async ({ emailAddress: email }) => {
    setError('');
    setLoading(true);
    try {
      await forgotPassword({ emailAddress: email }).unwrap();
      setEmailAddress(email);
      setCodeSent(true);
    } catch {
      setEmailAddress(email);
      setCodeSent(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async ({ code, newPassword, newPasswordConfirmed }) => {
    setError('');
    setLoading(true);
    try {
      const normalizedCode = (code || '').replace(/\D/g, '').slice(0, 6);
      await verifyForgotPasswordCode({
        emailAddress,
        code: normalizedCode,
        newPassword,
        newPasswordConfirmed,
      }).unwrap();
      setSuccess(true);
    } catch (err) {
      const msg = err.data?.title || err.data?.message || 'Failed to reset password. The code may be invalid or expired.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    setLoading(true);
    try {
      await forgotPassword({ emailAddress }).unwrap();
    } catch {
      // Keep a silent success posture to avoid revealing account existence.
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <CenteredCard>
        <Box sx={{ textAlign: 'center' }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h5" fontWeight={700} gutterBottom>Password Reset</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your password has been reset successfully. You can now sign in with your new password.
          </Typography>
          <Button variant="contained" fullWidth onClick={() => navigate('/login')}>
            Go to Sign In
          </Button>
        </Box>
      </CenteredCard>
    );
  }

  if (!codeSent) {
    return (
      <CenteredCard>
        <Typography variant="h5" fontWeight={700} align="center" gutterBottom>
          Forgot Password
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Enter your email and we will send a 6-digit verification code.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <FormProvider methods={requestMethods} onSubmit={handleRequestCode} autoComplete="off">
          <TextField
            name="emailAddress"
            label="Email Address"
            type="email"
            placeholder="you@company.com"
            disabled={loading}
            size="small"
            sx={{ mb: 3 }}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <MarkEmailReadIcon />}
          >
            {loading ? 'Sending...' : 'Send Verification Code'}
          </Button>
        </FormProvider>

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 3 }}>
          Remember your password?{' '}
          <Link component={RouterLink} to="/login" underline="hover">Sign in</Link>
        </Typography>
      </CenteredCard>
    );
  }

  return (
    <CenteredCard>
      <Typography variant="h5" fontWeight={700} align="center" gutterBottom>
        Reset Your Password
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
        Enter the 6-digit code sent to <strong>{emailAddress}</strong> and choose a new password.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <FormProvider methods={verifyMethods} onSubmit={handleSubmit} autoComplete="off">
        <VerificationCodeField
          name="code"
          label="Enter Your 6-Digit Verification Code"
          disabled={loading}
          sx={{ mb: 2 }}
        />
        <PasswordField
          name="newPassword"
          label="New Password"
          placeholder="Enter a new password"
          disabled={loading}
          size="small"
          autoComplete="new-password"
          sx={{ mb: 2 }}
        />

        <PasswordField
          name="newPasswordConfirmed"
          label="Confirm Password"
          placeholder="Confirm your new password"
          disabled={loading}
          size="small"
          autoComplete="new-password"
          sx={{ mb: 3 }}
        />

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={loading}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <LockIcon />}
          sx={{ mb: 1.5 }}
        >
          {loading ? 'Resetting...' : 'Reset Password'}
        </Button>
      </FormProvider>

      <Stack direction="row" justifyContent="center">
        <Button variant="text" size="small" onClick={resendCode} disabled={loading}>
          Resend code
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
        Back to{' '}
        <Link component={RouterLink} to="/login" underline="hover">Sign in</Link>
      </Typography>
    </CenteredCard>
  );
};

export default ResetPasswordPage;
